import React from 'react';
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  BarChart3, 
  Share2, 
  MessageSquare, 
  Bot, 
  ShieldCheck, 
  Zap,
  ArrowRight,
  Database,
  Globe,
  Settings,
  Headphones
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

export default function CRMPage() {
  return (
    <div className="min-h-screen bg-parchment">
      <Navbar />
      <main className="pt-20">
        {/* 1. HERO SECTION - THE OS CONCEPT */}
        <section className="py-24 bg-ink text-parchment border-b-2 border-ink relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 grain-texture pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl">
              <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-6 block">Plataforma Todo-en-Uno</span>
              <h1 className="text-6xl md:text-8xl font-display font-black uppercase italic leading-none mb-8">
                El OS Inmobiliario <br /> <span className="text-terracotta text-stroke-parchment">Propiedades LATAM</span>
              </h1>
              <p className="text-2xl font-serif italic opacity-60 leading-relaxed max-w-2xl mb-12">
                No es solo un CRM. Es el sistema operativo diseñado para el corredor moderno que integra IA, Tasaciones, Asistencia Legal y Compra Directa en un solo flujo de trabajo.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button variant="terracotta" size="lg" className="h-16 px-12 text-lg group">
                  EXPLORAR DASHBOARD <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="outline" size="lg" className="h-16 px-12 text-lg border-parchment text-parchment hover:bg-parchment hover:text-ink">
                  VER PLANES B2B
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* 2. ECOSYSTEM INTEGRATION SECTION */}
        <section className="py-32 bg-parchment">
          <div className="container mx-auto px-4">
            <div className="text-center mb-24">
              <h2 className="text-5xl md:text-7xl font-display font-black uppercase italic text-ink leading-tight">Un Ecosistema <br /> Sin Fisuras</h2>
              <p className="text-xl font-serif italic text-ink/60 mt-6 max-w-2xl mx-auto">Cada servicio de Propiedades LATAM vive dentro de tu panel de control, comunicándose entre sí para maximizar tu productividad.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-1">
              {[
                {
                  title: "Inteligencia IA",
                  desc: "Asistentes que califican leads y agendan visitas 24/7 vinculados a tu agenda.",
                  icon: Bot,
                  color: "bg-ink",
                  path: "/servicios/ecosistema-digital"
                },
                {
                  title: "Tasación IA",
                  desc: "Genera informes profesionales HousePricing en segundos para tus captaciones.",
                  icon: BarChart3,
                  color: "bg-terracotta",
                  path: "/servicios/valorizacion"
                },
                {
                  title: "Venta Directa",
                  desc: "Envía propiedades al comité de compra directa de Propiedades LATAM con un click.",
                  icon: Zap,
                  color: "bg-moss",
                  path: "/servicios/vender-directo"
                },
                {
                  title: "Soporte Legal",
                  desc: "Tickets directos a nuestros abogados para revisión de mandatos y escrituras.",
                  icon: ShieldCheck,
                  color: "bg-ink",
                  path: "/servicios/legal-contable"
                },
                {
                  title: "Soporte Contable",
                  desc: "Consultas tributarias y contables para la correcta facturación y gestión de honorarios.",
                  icon: Headphones,
                  color: "bg-terracotta",
                  path: "/servicios/legal-contable"
                }
              ].map((service, i) => (
                <div key={i} className="brutalist-border p-10 bg-white hover:bg-parchment transition-all group flex flex-col h-full">
                  <div className={cn("w-16 h-16 flex items-center justify-center mb-8 text-parchment transition-transform group-hover:-rotate-6", service.color)}>
                    <service.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-display font-black uppercase italic mb-4">{service.title}</h3>
                  <p className="font-serif italic text-muted-foreground mb-8 flex-1">{service.desc}</p>
                  <Link to={service.path} className="text-[10px] font-black uppercase tracking-widest text-terracotta flex items-center gap-2 group/link">
                    VER MÓDULO <ArrowRight className="h-3 w-3 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. CORE CRM FUNCTIONALITIES (REAL) */}
        <section className="py-32 bg-ink text-parchment">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row justify-between items-end mb-24 gap-8">
              <div className="max-w-2xl">
                <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-6 block">Funcionalidad Core</span>
                <h2 className="text-5xl md:text-7xl font-display font-black uppercase italic leading-none">Gestión Inmobiliaria <br /> de Alto Rendimiento</h2>
              </div>
              <p className="text-xl font-serif italic text-parchment/60 max-w-sm lg:text-right">Herramientas probadas para gestionar tu oficina y equipo de forma profesional.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-parchment/10">
              {[
                {
                  title: "Sincronización de Portales",
                  desc: "Publica en un solo lugar y sincroniza automáticamente con los principales portales de Chile y LATAM.",
                  icon: Globe
                },
                {
                  title: "Gestión de Clientes (Kyc)",
                  desc: "Base de datos inteligente con perfiles detallados, historial de búsqueda y calificación financiera.",
                  icon: Users
                },
                {
                  title: "Órdenes de Visita Digitales",
                  desc: "Genera, envía y firma órdenes de visita desde el móvil de forma instantánea y legal.",
                  icon: FileText
                },
                {
                  title: "Informes a Propietarios",
                  desc: "Automatiza el envío de reportes semanales con métricas de interés, visitas y feedback.",
                  icon: BarChart3
                },
                {
                  title: "Matching Automático",
                  desc: "El sistema cruza tus nuevos ingresos con los requerimientos activos de todos tus clientes.",
                  icon: Database
                },
                {
                  title: "Panel de Post-Venta",
                  desc: "Seguimiento riguroso desde la oferta aceptada hasta la inscripción en el conservador.",
                  icon: Settings
                }
              ].map((feat, i) => (
                <div key={i} className="p-12 bg-ink hover:bg-terracotta/10 transition-colors flex gap-8 items-start group">
                  <div className="p-4 brutalist-border-sm border-parchment/20 group-hover:border-terracotta transition-colors">
                    <feat.icon className="h-6 w-6 text-terracotta" />
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-2xl font-display font-black uppercase italic">{feat.title}</h4>
                    <p className="font-serif italic text-parchment/60 leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. CALL TO ACTION */}
        <section className="py-32 bg-parchment">
          <div className="container mx-auto px-4">
            <div className="brutalist-border bg-terracotta p-12 md:p-24 text-center space-y-8 relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 grain-texture pointer-events-none" />
              <h2 className="text-5xl md:text-8xl font-display font-black uppercase italic leading-none text-parchment relative z-10">
                Lleva tu negocio <br /> al siguiente nivel
              </h2>
              <p className="text-2xl font-serif italic text-parchment/80 max-w-2xl mx-auto relative z-10">
                Únete a la red de corredores que están transformando el mercado inmobiliario con tecnología de punta.
              </p>
              <div className="flex justify-center gap-4 relative z-10 pt-8">
                <Button size="lg" className="h-16 px-12 text-lg bg-ink text-parchment hover:bg-parchment hover:text-ink transition-colors">
                  AGENDAR DEMOSTRACIÓN
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
